import { MecordColors } from "Constants/Color";
import React from "react";
import { Table } from "react-bootstrap";

type ColumnConfig<T = any> = {
  label: string;
  key?: string; // dot notation supported
  render?: (row: T, index: number) => React.ReactNode;
  align?: "left" | "center" | "right";
  className?: string;
};

interface CommonTableProps<T = any> {
  columns: ColumnConfig<T>[];
  data: T[];
  showSrNo?: boolean;

  // ✅ Styling (optional)
  variant?: "bootstrap" | "plain";
  tableClassName?: string;
  headerClassName?: string;
  rowClassName?: string | ((row: T, index: number) => string);

  // ✅ Future-proof hooks
  onRowClick?: (row: T, index: number) => void;
}

const getValueByPath = (obj: any, path?: string) => {
  if (!path) return "—";
  return path.split(".").reduce((acc, key) => acc?.[key], obj) ?? "—";
};

const CommonTable = <T,>({
  columns,
  data,
  showSrNo = true,
  variant = "bootstrap",
  tableClassName = "",
  headerClassName = "",
  rowClassName = "",
  onRowClick,
}: CommonTableProps<T>) => {
  if (!data || data.length === 0) {
    return <div className="text-muted">No data available</div>;
  }

  const isBootstrap = variant === "bootstrap";

  return (
    <div className="table-responsive">
      <style>
        {`
                /* Optional – use only if needed */
.common-table {
  font-family: "Poppins", sans-serif;
  font-size: 13px;
}

.common-table thead th {
  background-color: ${MecordColors.LightGrayColor};
  color: ${MecordColors.NormalText};
  font-weight: 500;
}

.common-table tbody tr:hover {
  background-color: #fff5f5;
}

.common-table td,
.common-table th {
  vertical-align: middle;
}

            `}
      </style>
      <Table
        striped={isBootstrap}
        bordered={isBootstrap}
        hover={isBootstrap}
        className={tableClassName}
      >
        <thead className={headerClassName}>
          <tr>
            {/* {showSrNo && <th>Sr. No.</th>} */}
            {columns.map((col, index) => (
              <th
                key={index}
                style={{ textAlign: col.align || "left" }}
                className={col.className}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={
                typeof rowClassName === "function"
                  ? rowClassName(row, rowIndex)
                  : rowClassName
              }
              onClick={() => onRowClick?.(row, rowIndex)}
              style={{ cursor: onRowClick ? "pointer" : "default" }}
            >
              {/* {showSrNo && <td>{rowIndex + 1}</td>} */}

              {columns.map((col, colIndex) => (
                <td
                  key={colIndex}
                  style={{ textAlign: col.align || "left" }}
                  className={col.className}
                >
                  {col.render
                    ? col.render(row, rowIndex)
                    : getValueByPath(row, col.key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default CommonTable;
